import React, { useState, useEffect, useCallback } from 'react';
import { v2ConnectorsService } from '../services/v2ConnectorsService';
import marketplacesettingsService from '../services/marketplacesettingsService';

// Test tab: run the connector's entry point on the server against either an existing credential or
// settings entered manually (fields sourced from the marketplace's marketplacesettings schema).
const scopeLabel = (c) => c.hotel_id ? `hotel ${c.hotel_id}` : c.account_id ? `chain ${c.account_id}` : c.partner_id ? `partner ${c.partner_id}` : 'global';

const ConnectorTest = ({ connectorId, marketplaceId, entrypoint }) => {
  const [mode, setMode] = useState('credential'); // 'credential' | 'manual'
  const [credentials, setCredentials] = useState([]);
  const [credentialId, setCredentialId] = useState('');
  const [schema, setSchema] = useState([]);
  const [manualValues, setManualValues] = useState({});
  const [envText, setEnvText] = useState('');
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const [output, setOutput] = useState(null);

  const loadInfo = useCallback(async () => {
    if (!connectorId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await v2ConnectorsService.getTestInfo(connectorId);
      setCredentials(res.credentials || []);
    } catch (err) {
      setError(err.message || 'Failed to load credentials');
    } finally {
      setLoading(false);
    }
  }, [connectorId]);

  useEffect(() => { loadInfo(); }, [loadInfo]);

  // Field definitions for manual entry come from the marketplace's settings schema.
  useEffect(() => {
    if (!marketplaceId) { setSchema([]); return; }
    marketplacesettingsService.getSchema({ MARKETPLACEID: marketplaceId })
      .then(res => setSchema(res.settings || []))
      .catch(() => setSchema([]));
  }, [marketplaceId]);

  const setManual = (name, value) => setManualValues(v => ({ ...v, [name]: value }));

  const parseEnv = (text) => {
    const env = {};
    text.split('\n').forEach(line => {
      const t = line.trim();
      if (!t || t.startsWith('#')) return;
      const i = t.indexOf('=');
      if (i > 0) env[t.slice(0, i).trim()] = t.slice(i + 1).trim();
    });
    return env;
  };

  const run = async () => {
    setRunning(true);
    setError(null);
    setOutput(null);
    try {
      const payload = mode === 'manual' ? { settings: manualValues } : { credentialId };
      const env = parseEnv(envText);
      if (Object.keys(env).length) payload.env = env;
      const res = await v2ConnectorsService.runTest(connectorId, payload);
      setOutput(res);
    } catch (err) {
      setError(err.message || 'Test request failed');
    } finally {
      setRunning(false);
    }
  };

  const canRun = !!entrypoint && !running && (mode === 'credential' ? !!credentialId : schema.length > 0);

  if (!connectorId) {
    return <div style={{ background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 10, padding: '1.25rem', color: '#6b7280', fontSize: '0.9rem' }}>Save the connector first, then you can test it here.</div>;
  }

  const tabBtn = (m, label) => (
    <button type="button" onClick={() => setMode(m)}
      style={{ padding: '0.35rem 0.8rem', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', border: '1px solid #d1d5db', background: mode === m ? '#eef2ff' : '#fff', color: mode === m ? '#4338ca' : '#374151', borderRadius: 6 }}>
      {label}
    </button>
  );

  return (
    <div>
      <p style={{ color: '#6b7280', fontSize: '0.8rem', margin: '0 0 12px' }}>
        Runs this connector’s entry point (<code>{entrypoint || 'not set'}</code>) and shows the return value and logs.
      </p>

      {error && <div className="error" style={{ marginBottom: 8 }}>{error}</div>}

      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        {tabBtn('credential', 'Existing credential')}
        {tabBtn('manual', 'Enter manually')}
      </div>

      {mode === 'credential' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 12, maxWidth: 360 }}>
          <label style={{ fontSize: '0.8rem', fontWeight: 500, color: '#374151' }}>Credential</label>
          <select value={credentialId} onChange={e => setCredentialId(e.target.value)} disabled={loading || running}
            style={{ padding: '0.4rem 0.5rem', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.85rem' }}>
            <option value="">{loading ? 'Loading…' : credentials.length ? 'Select a credential…' : 'No credentials for this marketplace'}</option>
            {credentials.map(c => <option key={c.id} value={c.id}>{c.credential_name} ({scopeLabel(c)})</option>)}
          </select>
        </div>
      ) : (
        <div style={{ marginBottom: 12, maxWidth: 520 }}>
          {schema.length === 0 ? (
            <div style={{ fontSize: '0.82rem', color: '#9ca3af' }}>
              {marketplaceId ? 'No settings defined for this marketplace.' : 'This connector has no marketplace.'}
            </div>
          ) : schema.map(s => {
            const name = s.SETTINGNAME;
            const label = s.SETTINGLABEL || name;
            const opts = s.DATATYPE === 'SELECT' && s.AVAILABLEVALUES ? String(s.AVAILABLEVALUES).split(',').map(x => x.trim()).filter(Boolean) : null;
            return (
              <div key={name} style={{ display: 'flex', flexDirection: 'column', gap: 3, marginBottom: 8 }}>
                <label style={{ fontSize: '0.8rem', fontWeight: 500, color: '#374151' }}>
                  {label}{s.required === 'Y' && <span style={{ color: '#dc2626' }}> *</span>}
                </label>
                {opts ? (
                  <select value={manualValues[name] || ''} onChange={e => setManual(name, e.target.value)}
                    style={{ padding: '0.4rem 0.5rem', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.85rem' }}>
                    <option value="">— Select —</option>
                    {opts.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <input type="text" value={manualValues[name] || ''} onChange={e => setManual(name, e.target.value)}
                    placeholder={s.PLACEHOLDER || ''}
                    style={{ padding: '0.4rem 0.5rem', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.85rem' }} />
                )}
                {s.HELPERTEXT && <span style={{ fontSize: '0.72rem', color: '#9ca3af' }}>{s.HELPERTEXT}</span>}
              </div>
            );
          })}
        </div>
      )}

      <div style={{ marginBottom: 12, maxWidth: 520 }}>
        <label style={{ fontSize: '0.8rem', fontWeight: 500, color: '#374151', display: 'block', marginBottom: 4 }}>Environment variables (optional)</label>
        <textarea value={envText} onChange={e => setEnvText(e.target.value)} rows={2} spellCheck={false}
          placeholder={'TEST_CREDENTIAL_ID=42'}
          style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #d1d5db', borderRadius: 6, padding: '0.4rem 0.5rem', fontSize: '0.8rem', fontFamily: 'ui-monospace, Menlo, monospace' }} />
        <span style={{ fontSize: '0.72rem', color: '#9ca3af' }}>KEY=value per line — set on <code>process.env</code> for this run.</span>
      </div>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12 }}>
        <button type="button" onClick={run} disabled={!canRun}
          style={{ padding: '0.45rem 1rem', fontSize: '0.85rem', fontWeight: 600, color: '#fff', background: canRun ? '#16a34a' : '#9ca3af', border: 'none', borderRadius: 7, cursor: canRun ? 'pointer' : 'default' }}>
          {running ? 'Running…' : '▶ Run test'}
        </button>
        {!entrypoint && <span style={{ fontSize: '0.78rem', color: '#b45309' }}>Set an entry point on the Code tab first.</span>}
      </div>

      {output && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <span style={{ display: 'inline-block', padding: '0.2rem 0.6rem', borderRadius: 999, fontSize: '0.78rem', fontWeight: 700, color: output.ok ? '#166534' : '#b91c1c', background: output.ok ? '#dcfce7' : '#fee2e2' }}>
              {output.ok ? 'SUCCESS' : 'FAILED'}
            </span>
            {typeof output.durationMs === 'number' && <span style={{ fontSize: '0.78rem', color: '#6b7280' }}>{output.durationMs} ms</span>}
          </div>

          {!output.ok && output.error && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: 8, padding: '0.6rem 0.8rem', fontSize: '0.82rem', marginBottom: 10, whiteSpace: 'pre-wrap', fontFamily: 'ui-monospace, Menlo, monospace' }}>
              {output.error}{output.stack ? '\n\n' + output.stack : ''}
            </div>
          )}

          {output.ok && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#374151', marginBottom: 4 }}>Result</div>
              <pre style={{ background: '#0f172a', color: '#e2e8f0', padding: '0.7rem', borderRadius: 8, overflowX: 'auto', fontSize: '0.75rem', margin: 0 }}>
                {JSON.stringify(output.result, null, 2)}
              </pre>
            </div>
          )}

          <div>
            <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#374151', marginBottom: 4 }}>Logs ({(output.logs || []).length})</div>
            {(output.logs || []).length === 0 ? (
              <div style={{ fontSize: '0.8rem', color: '#9ca3af' }}>No console output.</div>
            ) : (
              <pre style={{ background: '#f8fafc', border: '1px solid #e5e7eb', padding: '0.7rem', borderRadius: 8, overflowX: 'auto', fontSize: '0.75rem', margin: 0, whiteSpace: 'pre-wrap' }}>
                {output.logs.join('\n')}
              </pre>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ConnectorTest;
